import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Abrufausfall, useAbruf } from './Abruf'
import { api } from './api'
import { ausStammdatum, mitCode } from './Auswahlfeld'
import type { Auswahleintrag } from './Auswahlfeld'
import { fachlicherSatz } from './Fehlersatz'
import { useListenort } from './Listenort'
import { useDarf } from './Rechte'
import { Baumzeile, berichtsstand } from './Gliederungsbaum'
import type { Rangquelle } from './Gliederungsbaum'
import { Strukturanlage } from './Strukturanlage'
import type { Angelegt, Anlageziel } from './Strukturanlage'
import { Zeilenmenue } from './Zeilenmenue'
import { beschriftung, istStatistikkontoImKatalog, t } from './sprache'
import { angeboten, laufenderMonat } from './Stilllegung'
import { mitBis } from './Bisvermerk'
import { normalisiere } from './Suche'
import { zielzeilen } from './Zuordnungsfenster'
import type { Gliederungsbild, Gliederungsknoten, Positionsplan } from './typen'

/**
 * Ein Konto wählen — **aus dem Baum von Bilanz und GuV** (7.2 Abschnitt 33,
 * R1043–R1047).
 *
 * **Der Befund des Auftraggebers vom 2026-10-04:** *„Ich möchte die Auswahl
 * zudem auf dem ‚Baum' der Bilanz/GuV auswählen können. Andernfalls wird das
 * Finden eines Kontos später zur Qual, wenn ein großer Konzern drin ist."*
 *
 * **Geschlossen ist es ein Auswahlfeld wie jedes andere**, geöffnet zeigt die
 * Liste den Baum — offen wie im Bericht, mit offenem Weg zum gewählten Konto
 * (R1043, R1857), und gezeichnet wie der Bericht (7.2 Abschnitt 55, R1855). Wer tippt, sieht die Treffer mit ihren
 * Oberpositionen (R1044). Konten ohne Position stehen im Ast „Ohne Position"
 * am Ende und bleiben wählbar (R1045).
 *
 * **Der Baum ist nicht neu gebaut.** Welche Zeile unter welcher steht und was
 * eine Suche trifft, rechnet `zielzeilen` — dieselbe Stelle, die das
 * Zuordnungsfenster (R603) und das Fenster für die Abgrenzung (R826) benutzen.
 *
 * **Angeboten wird nur, was `eintraege` nennt.** Die Stelle sagt, welche
 * Konten zulässig sind; eine Position, unter der keines davon hängt, erscheint
 * nicht.
 *
 * **Derselbe Baum wählt auch eine Position** (`waehlt="position"`, 2.7
 * Abschnitt 3.7a, R1432): Dann sind die zulässigen Positionen die Blätter,
 * und Konten stehen nicht darin — das Saldowechselziel ist eine Position.
 *
 * **Was fehlt, entsteht in der Liste** (7.2 Abschnitt 47, R1493–R1497): Ein
 * Rechtsklick auf eine Position bietet dasselbe wie am Bericht — *Neue
 * Unterposition …* und *Neues Konto …* — im selben Fenster. Das neue Konto
 * steht danach im Feld; gespeichert wird die Seite mit ihrem eigenen Knopf.
 */
export function Kontofeld({
  wert,
  eintraege: alleEintraege,
  aufWahl,
  ledger,
  periode,
  leer,
  id,
  className,
  beschriftung: aria,
  titel,
  abgeschaltet = false,
  autoFocus = false,
  waehlt = 'konto',
  unter,
}: {
  wert: string
  /** Die zulässigen Konten — oder Positionen —, beschriftet wie an der Stelle üblich. */
  eintraege: Auswahleintrag[]
  aufWahl: (wert: string) => void
  /**
   * Bewertungsebene und Monat, deren Baum gezeigt wird. Fehlt die Ebene, ist
   * es die Gliederung zum laufenden Monat, bei mehreren mit Umschalter (R1047).
   */
  ledger?: string
  periode?: number
  /** Der Eintrag für „nichts gewählt" — fehlt er, gibt es ihn nicht. */
  leer?: string
  id?: string
  className?: string
  /** `aria-label`, wo kein `<label>` danebensteht. */
  beschriftung?: string
  titel?: string
  abgeschaltet?: boolean
  autoFocus?: boolean
  /** Was gewählt wird: ein Konto, oder eine Position aus demselben Baum (R1432). */
  waehlt?: 'konto' | 'position' | 'beides'
  /**
   * Nur Konten unter dieser Position, auch in ihren Unterpositionen — etwa das
   * Konto, auf dem eine Ausweiskorrektur entlastet (2.9 Abschnitt 15.9, R1617).
   */
  unter?: string
}) {
  const positionen = waehlt === 'position'
  // **Position oder Konto in einem Feld** (2.9 Abschnitt 15.9, R1616): Die
  // Position ist wählbar und klappt trotzdem zu ihren Konten auf.
  const beides = waehlt === 'beides'
  // Was hier eben angelegt wurde, steht schon zur Wahl, bevor die Seite ihre
  // Liste neu holt (R1495).
  const [neu, setNeu] = useState<Auswahleintrag[]>([])
  // **Nur was in der Periode gilt** (R1447): ohne Periode der laufende Monat.
  // Ein schon gewähltes Stillgelegtes bleibt mit Vermerk stehen.
  const eintraege = useMemo(() => {
    const da = new Set(alleEintraege.map((e) => e.wert))
    return angeboten([...alleEintraege, ...neu.filter((e) => !da.has(e.wert))], wert, periode ?? laufenderMonat())
  }, [alleEintraege, neu, wert, periode])
  const kennung = useId()
  const baumId = `${kennung}-baum`
  const feld = useRef<HTMLInputElement>(null)
  const rahmen = useRef<HTMLDivElement>(null)

  const [offen, setOffen] = useState(false)
  const [eingabe, setEingabe] = useState('')
  /** Ob die Eingabe ein Filter ist — beim Aufklappen steht das Gewählte markiert im Feld (R586). */
  const [getippt, setGetippt] = useState(false)
  const [zeiger, setZeiger] = useState(0)
  const [aufgeklappt, setAufgeklappt] = useState<ReadonlySet<string>>(new Set())
  /** Ob das gewählte Konto im zugeklappten Feld gekürzt dasteht — dann steht es ganz als Hinweis (R1701). */
  const [gekuerzt, setGekuerzt] = useState(false)
  const markiereBeimZeichnen = useRef(false)

  /*
    **Anlegen aus der Liste** (R1493): das Menü an einer Position, das Fenster
    vom Bericht, und die leere Unterposition, die bis zum Schließen der Liste
    sichtbar bleibt (R1497).
  */
  const darf = useDarf()
  const [menue, setMenue] = useState<{ x: number; y: number; knoten: Gliederungsknoten } | null>(null)
  const [anlage, setAnlage] = useState<Anlageziel | null>(null)
  const [frisch, setFrisch] = useState<string | null>(null)
  const frischVorgemerkt = useRef(false)
  const nachDemFenster = useRef<string | null>(null)
  const [stand, setStand] = useState(0)

  const quelle = useGliederung(offen, ledger, periode, stand)
  // Wo die Statistikkonten hängen — nur, wenn welche zur Wahl stehen (R1733).
  const orte = useStatistikorte(offen && alleEintraege.some((e) => istStatistikkontoImKatalog(e.wert)))

  const nachWert = useMemo(() => new Map(eintraege.map((e) => [e.wert, e])), [eintraege])
  const anzeige = wert === '' ? (leer ?? '') : (nachWert.get(wert)?.text ?? '')
  const suche = getippt ? eingabe.trim() : ''

  /*
    **Nur die zulässigen Konten — aber der ganze Baum** (7.2 Abschnitt 52,
    R1612, R1613). Bis zum 2026-10-12 fehlte ein Ast ohne wählbares Konto;
    der Baum sah in jedem Feld anders aus, und unter einer Position ohne Konto
    ließ sich nichts anlegen. Jetzt steht jede Zeile des Berichts an ihrer
    Stelle, und was darunter nichts Wählbares trägt, steht blass da.
  */
  const baum = useMemo(() => {
    if (!quelle.bild) {
      return null
    }

    // Beim Wählen einer Position trägt der Baum keine Konten; wählbar ist der
    // Knoten selbst.
    const imAst = unter ? Ast(quelle.bild.knoten, unter) : null
    const mitKonten = quelle.bild.knoten.map((k) => ({
      ...k,
      konten:
        positionen || (imAst && !imAst.has(k.schluessel)) ? [] : k.konten.filter((kt) => nachWert.has(kt.schluessel)),
    }))
    const waehlbar = (k: Gliederungsknoten) =>
      positionen ? nachWert.has(k.schluessel) : k.konten.length > 0 || (beides && nachWert.has(k.schluessel))
    const knoten = mitKonten
    const leer = new Set(
      mitKonten
        .filter((k, i) => {
          for (let j = i; j < mitKonten.length; j++) {
            const unter = mitKonten[j]
            if (!unter || (j > i && unter.ebene <= k.ebene)) {
              break
            }
            if (waehlbar(unter) || unter.schluessel === frisch) {
              return false
            }
          }
          return true
        })
        .map((k) => k.schluessel),
    )

    // Eine Position aus einer anderen Gliederung steht in deren Baum, nicht
    // im Ast „Ohne Position" — wer sie sucht, schaltet die Gliederung um.
    const imBaum = new Set(
      positionen ? knoten.map((k) => k.schluessel) : knoten.flatMap((k) => k.konten.map((kt) => kt.schluessel)),
    )
    const auchPositionen = beides ? new Set(knoten.map((k) => k.schluessel)) : new Set<string>()
    // Unter einer Position liegt kein Konto ohne Position (R1617).
    const ausserhalb =
      positionen || imAst ? [] : eintraege.filter((e) => !imBaum.has(e.wert) && !auchPositionen.has(e.wert))
    return {
      knoten,
      leer,
      // **Ein Statistikkonto hat seine Position in der Kapitalflussrechnung**
      // (2.1 Abschnitt 3.6, R1733) — es steht dort und nicht unter „Ohne
      // Position". Bis zum 2026-10-14 stand es dort, mit dem Satz, es
      // erscheine in keinem Bericht.
      statistik: ausserhalb.filter((e) => istStatistikkontoImKatalog(e.wert)),
      ohne: ausserhalb.filter((e) => !istStatistikkontoImKatalog(e.wert)),
    }
  }, [quelle.bild, nachWert, eintraege, positionen, beides, frisch, unter])

  const zeilen = useMemo<Zeile[]>(() => {
    const worte = normalisiere(suche).split(/\s+/).filter((w) => w.length > 0)
    const passt = (e: Auswahleintrag) => {
      const heu = normalisiere(`${e.text} ${e.suchtext ?? ''}`)
      return worte.every((w) => heu.includes(w))
    }
    const konto = (e: Auswahleintrag, ebene: number): Zeile => ({ art: 'konto', wert: e.wert, text: e.text, ebene })

    // Ohne Gliederung bleibt die Liste wählbar — flach, wie vor dem Baum.
    // Auch, wenn sie nicht kam (R1210): Die Konten selbst sind ja da.
    if (baum === null) {
      return quelle.fehler === null && !quelle.ausgefallen ? [] : eintraege.filter(passt).map((e) => konto(e, 0))
    }

    const liste: Zeile[] = []
    if (leer !== undefined && worte.length === 0) {
      liste.push({ art: 'konto', wert: '', text: leer, ebene: 0 })
    }

    for (const bericht of ['bilanz', 'guv'] as const) {
      const reihe = zielzeilen(baum.knoten, bericht, aufgeklappt, suche)

      // **Oben stehen Aktiva, Passiva und die Gewinn- und Verlustrechnung**
      // (R1612): Die GuV hat keinen eigenen Wurzelknoten im Plan, ihre
      // Posten stehen darunter wie im Bericht.
      const tiefer = bericht === 'guv' ? 1 : 0
      if (bericht === 'guv' && reihe.length > 0) {
        const guvOffen = worte.length > 0 || aufgeklappt.has(GUV)
        // Blass wie jeder Ast, unter dem nichts wählbar ist (R1612).
        const guvLeer = baum.knoten
          .filter((k) => k.art === 'ertrag' || k.art === 'aufwand')
          .every((k) => baum.leer.has(k.schluessel))
        liste.push({
          art: 'position',
          schluessel: GUV,
          text: t('kontofeld.guv'),
          ebene: 0,
          offen: guvOffen,
          hatKinder: true,
          leer: guvLeer,
        })
        if (!guvOffen) {
          continue
        }
      }

      for (const z of reihe) {
        const ebene = z.knoten.ebene + tiefer
        // Die wählbare Position steht als Eintrag, nicht als Ast (R1432).
        const eintrag = positionen ? nachWert.get(z.knoten.schluessel) : undefined
        if (eintrag) {
          liste.push({ art: 'konto', wert: eintrag.wert, text: positionsname(z.knoten), ebene })
          continue
        }
        liste.push({
          art: 'position',
          schluessel: z.knoten.schluessel,
          text: positionsname(z.knoten),
          ebene,
          rang: z.knoten,
          offen: z.offen,
          hatKinder: z.hatKinder,
          frisch: z.knoten.schluessel === frisch,
          leer: baum.leer.has(z.knoten.schluessel),
          waehlbar: beides && nachWert.has(z.knoten.schluessel),
        })
        for (const kt of z.konten) {
          const e = nachWert.get(kt.schluessel)
          if (e) {
            liste.push(konto(e, ebene + 1))
          }
        }
      }
    }

    // **Der Ast Kapitalflussrechnung** (R1733): die Positionen, unter denen
    // Statistikkonten hängen, in der Reihenfolge des Berichts, und darunter
    // die Konten mit „Stat." davor. Solange die Positionen nicht da sind,
    // stehen die Konten unmittelbar im Ast.
    const statistik = worte.length > 0 ? baum.statistik.filter(passt) : baum.statistik
    if (statistik.length > 0) {
      const kfrOffen = worte.length > 0 || aufgeklappt.has(KFR)
      liste.push({ art: 'position', schluessel: KFR, text: t('kontofeld.kfr'), ebene: 0, offen: kfrOffen, hatKinder: true })
      if (kfrOffen) {
        const stat = (e: Auswahleintrag, ebene: number): Zeile => konto({ ...e, text: `${t('element.kurz.statistik')} ${e.text}` }, ebene)
        const ohneOrt = statistik.filter((e) => !orte?.has(e.wert))
        const jeOrt = new Map<string, { ort: Statistikort; konten: Auswahleintrag[] }>()
        for (const e of statistik) {
          const ort = orte?.get(e.wert)
          if (ort) {
            const gruppe = jeOrt.get(ort.zeile) ?? { ort, konten: [] }
            gruppe.konten.push(e)
            jeOrt.set(ort.zeile, gruppe)
          }
        }
        for (const { ort, konten } of [...jeOrt.values()].sort((a, b) => a.ort.sortierung - b.ort.sortierung)) {
          const schluessel = KFR + ort.zeile
          const ortOffen = worte.length > 0 || aufgeklappt.has(schluessel)
          liste.push({ art: 'position', schluessel, text: ort.text, ebene: 1, offen: ortOffen, hatKinder: true })
          if (ortOffen) {
            liste.push(...konten.map((e) => stat(e, 2)))
          }
        }
        liste.push(...ohneOrt.map((e) => stat(e, 1)))
      }
    }

    const ohne = worte.length > 0 ? baum.ohne.filter(passt) : baum.ohne
    if (ohne.length > 0) {
      const ohneOffen = worte.length > 0 || aufgeklappt.has(OHNE)
      liste.push({
        art: 'position',
        schluessel: OHNE,
        text: t('kontofeld.ohneposition'),
        ebene: 0,
        offen: ohneOffen,
        hatKinder: true,
      })
      if (ohneOffen) {
        liste.push(...ohne.map((e) => konto(e, 1)))
      }
    }

    return liste
  }, [baum, quelle.fehler, quelle.ausgefallen, eintraege, nachWert, aufgeklappt, suche, leer, frisch, orte])

  /*
    **Beim Aufklappen steht der Baum offen wie im Bericht** (7.2 Abschnitt 55,
    R1857): jede Position mit Unterpositionen, dazu der Weg zum gewählten
    Konto (R1043). Bis zum 2026-10-16 stand nur dieser Weg offen. Die Äste,
    die der Bericht nicht kennt, folgen derselben Regel: die Gewinn- und
    Verlustrechnung und die Kapitalflussrechnung mit ihren Positionen offen,
    *Ohne Position* mit seinen Konten zu.
  */
  useEffect(() => {
    if (!offen || baum === null) {
      return
    }

    // Nach einer neuen Unterposition steht der Weg zu ihr offen (R1497).
    // Ein gewähltes Statistikkonto: der Ast Kapitalflussrechnung und seine
    // Position darin (R1733).
    const ort = orte?.get(wert)
    const weg = frisch
      ? wegZu(baum.knoten, [], frisch, true)
      : baum.statistik.some((e) => e.wert === wert)
        ? new Set(ort ? [KFR, KFR + ort.zeile] : [KFR])
        : wegZu(baum.knoten, baum.ohne, wert, positionen || (beides && baum.knoten.some((k) => k.schluessel === wert)))
    const bericht = berichtsstand(baum.knoten)
    bericht.add(GUV)
    if (baum.statistik.some((e) => orte?.has(e.wert))) {
      bericht.add(KFR)
    }
    setAufgeklappt(new Set([...bericht, ...weg]))
  }, [offen, baum, wert, positionen, beides, frisch, orte])

  // Der Zeiger steht auf dem gewählten Konto, beim Tippen auf dem ersten Kontotreffer (R1044).
  useEffect(() => {
    if (!offen) {
      return
    }

    const ziel = getippt
      ? zeilen.findIndex((z) => z.art === 'konto')
      : zeilen.findIndex((z) => z.art === 'konto' && z.wert === wert)
    if (ziel >= 0) {
      setZeiger(ziel)
    } else {
      setZeiger((z) => Math.min(z, Math.max(0, zeilen.length - 1)))
    }
    // Nur wenn sich die Suche oder der Baum ändert — nicht bei jedem Klappen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen, getippt, suche, baum])

  // Die neue Unterposition ist vorgemerkt, sobald sie im Baum steht — einmal (R1497).
  useEffect(() => {
    if (!offen || !frisch || frischVorgemerkt.current) {
      return
    }

    const i = zeilen.findIndex((z) => z.art === 'position' && z.schluessel === frisch)
    if (i >= 0) {
      frischVorgemerkt.current = true
      setZeiger(i)
    }
  }, [offen, frisch, zeilen])

  function oeffne(ersteTaste?: string) {
    if (abgeschaltet) {
      return
    }

    if (ersteTaste === undefined) {
      setEingabe(anzeige)
      setGetippt(false)
      markiereBeimZeichnen.current = true
    } else {
      setEingabe(ersteTaste)
      setGetippt(true)
    }
    setZeiger(0)
    setOffen(true)
  }

  /** Schließen, ohne zu wählen — was gewählt war, bleibt gewählt (R585). */
  function schliesse(fokus = true) {
    setOffen(false)
    setGetippt(false)
    setEingabe('')
    // Geschlossen ist die eben angelegte Position nicht mehr vorgemerkt (R1497).
    setFrisch(null)
    setMenue(null)
    if (fokus) {
      feld.current?.focus()
    }
  }

  function klappe(schluessel: string, auf?: boolean) {
    setAufgeklappt((bisher) => {
      const neu = new Set(bisher)
      if (auf ?? !neu.has(schluessel)) {
        neu.add(schluessel)
      } else {
        neu.delete(schluessel)
      }
      return neu
    })
  }

  function nimm(z: Zeile | undefined) {
    if (!z) {
      return
    }

    if (z.art === 'position') {
      // Im Feld für Position oder Konto wählt die Zeile, der Pfeil klappt (R1616).
      if (z.waehlbar) {
        schliesse()
        aufWahl(z.schluessel)
        return
      }
      if (z.hatKinder && !getippt) {
        klappe(z.schluessel)
      }
      return
    }

    schliesse()
    aufWahl(z.wert)
  }

  const knotenNach = useMemo(
    () => new Map((quelle.bild?.knoten ?? []).map((k) => [k.schluessel, k])),
    [quelle.bild],
  )
  // Was der Angemeldete nicht darf, steht nicht im Menü (R1180).
  const darfPosition = darf('unterpositionAnlegen')
  const darfKonto = !positionen && darf('kontoAnlegen')

  /**
   * Unter welcher Position eine Zeile anlegen lässt — wie am Bericht nicht
   * unter einer Formel und nicht am Ergebnis (R1233). **Ein Konto trägt kein
   * Menü** (R1496); im Positionsfeld ist die wählbare Zeile selbst eine Position.
   */
  function anlageort(z: Zeile | undefined): Gliederungsknoten | null {
    if (!z || (!darfPosition && !darfKonto)) {
      return null
    }

    const k = z.art === 'position' ? knotenNach.get(z.schluessel) : positionen ? knotenNach.get(z.wert) : undefined
    return k && !k.istRechenposition && k.istErgebnis !== true ? k : null
  }

  function zeigeMenue(i: number, x: number, y: number) {
    const k = anlageort(zeilen[i])
    if (k) {
      setZeiger(i)
      setMenue({ x, y, knoten: k })
    }
  }

  /** Das Fenster vom Bericht, mit dem Monat des Baums (R1494). */
  function rufeAnlage(art: 'konto' | 'position', k: Gliederungsknoten) {
    schliesse(false)
    setAnlage({ art, position: k.schluessel, positionsname: positionsname(k), periode: periode ?? laufenderMonat() })
  }

  function angelegt(a: Angelegt) {
    // Der Baum kommt frisch, mit dem Neuen darin.
    vergissGliederungen()
    setStand((s) => s + 1)
    if (a.art === 'kfrzeile') {
      return
    }

    if (a.art === 'position' && !positionen) {
      nachDemFenster.current = a.schluessel
      return
    }

    // Ein Konto — im Positionsfeld die Position — steht gleich im Feld (R1495, R1497).
    setNeu((bisher) => [...bisher, ausStammdatum({ schluessel: a.schluessel, code: '', namen: { de: a.bezeichnung } })])
    aufWahl(a.schluessel)
  }

  /** Nach einer neuen, leeren Unterposition geht die Liste wieder auf, mit ihr vorgemerkt (R1497). */
  function anlageZu() {
    setAnlage(null)
    const position = nachDemFenster.current
    nachDemFenster.current = null
    if (position) {
      frischVorgemerkt.current = false
      setFrisch(position)
      oeffne()
    } else {
      feld.current?.focus()
    }
  }

  useLayoutEffect(() => {
    if (offen && markiereBeimZeichnen.current) {
      markiereBeimZeichnen.current = false
      feld.current?.focus()
      feld.current?.select()
    }
  }, [offen])

  // Wo die Liste steht — an derselben Stelle gemessen wie beim Auswahlfeld und so breit wie ihre
  // längste Zeile (R1701), aber wenigstens breiter: Ein Baum braucht Platz für seine Einrückung.
  const ort = useListenort(offen, feld, rahmen, { mindestbreite: 380, hoechstens: 420, wenigstens: 160, kippAb: 200 }, zeilen)

  useEffect(() => {
    if (offen) {
      document.getElementById(`${kennung}-z${zeiger}`)?.scrollIntoView?.({ block: 'nearest' })
    }
  }, [offen, zeiger, kennung])

  // Ein Klick daneben schließt — und wählt nichts.
  useEffect(() => {
    if (!offen) {
      return
    }

    function daneben(e: MouseEvent) {
      const ziel = e.target as Node
      if (!feld.current?.contains(ziel) && !rahmen.current?.contains(ziel)) {
        schliesse(false)
      }
    }

    document.addEventListener('mousedown', daneben)
    return () => document.removeEventListener('mousedown', daneben)
  }, [offen])

  function beiTaste(e: React.KeyboardEvent<HTMLInputElement>) {
    if (abgeschaltet) {
      return
    }

    if (!offen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        oeffne()
        return
      }

      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault()
        oeffne(e.key)
      }
      return
    }

    const aktuell = zeilen[zeiger]

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setZeiger((z) => Math.min(zeilen.length - 1, z + 1))
        return
      case 'ArrowUp':
        e.preventDefault()
        setZeiger((z) => Math.max(0, z - 1))
        return
      case 'Home':
        e.preventDefault()
        setZeiger(0)
        return
      case 'End':
        e.preventDefault()
        setZeiger(Math.max(0, zeilen.length - 1))
        return
      case 'Enter':
        e.preventDefault()
        nimm(aktuell)
        return
      case 'Escape':
        // Darf nichts geändert haben — und nicht den Dialog schließen, in dem das Feld steht.
        e.preventDefault()
        e.stopPropagation()
        schliesse()
        return
      case 'Tab':
        // Wer weiterspringt, hat nicht gewählt (R585).
        schliesse(false)
        return
    }

    // Pfeil rechts und links klappen — solange nicht getippt wird; dann gehören sie der Schreibmarke.
    if (getippt || !aktuell) {
      return
    }

    if (e.key === 'ArrowRight' && aktuell.art === 'position' && aktuell.hatKinder) {
      e.preventDefault()
      if (aktuell.offen) {
        setZeiger((z) => Math.min(zeilen.length - 1, z + 1))
      } else {
        klappe(aktuell.schluessel, true)
      }
      return
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      if (aktuell.art === 'position' && aktuell.offen) {
        klappe(aktuell.schluessel, false)
        return
      }
      // Sonst zur Position darüber.
      for (let i = zeiger - 1; i >= 0; i--) {
        const z = zeilen[i]
        if (z && z.art === 'position' && z.ebene < aktuell.ebene) {
          setZeiger(i)
          return
        }
      }
    }
  }

  const planwahl = !ledger && quelle.plaene.length > 1
  const klassen = ['auswahlfeld', className].filter((k) => k).join(' ')

  return (
    <>
      <input
        ref={feld}
        id={id}
        type="text"
        role="combobox"
        className={klassen}
        value={offen ? eingabe : anzeige}
        readOnly={!offen}
        disabled={abgeschaltet}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
        title={titel ?? (gekuerzt && !offen ? anzeige : undefined)}
        aria-label={aria}
        aria-haspopup="tree"
        aria-expanded={offen}
        aria-controls={offen ? baumId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={offen && zeilen.length > 0 ? `${kennung}-z${zeiger}` : undefined}
        onClick={() => (offen ? schliesse() : oeffne())}
        onKeyDown={beiTaste}
        // Passt das gewählte Konto nicht ins Feld, steht es ganz als Hinweis (R1701).
        onMouseEnter={(e) => setGekuerzt(e.currentTarget.scrollWidth > e.currentTarget.clientWidth)}
        // Kontextmenü-Taste und Umschalt+F10 kommen am Feld an, nicht an der Zeile (R166).
        onContextMenu={(e) => {
          if (!offen) {
            return
          }

          e.preventDefault()
          const r = document.getElementById(`${kennung}-z${zeiger}`)?.getBoundingClientRect()
          if (r) {
            zeigeMenue(zeiger, r.left + 24, r.bottom)
          }
        }}
        onChange={(e) => {
          setEingabe(e.target.value)
          setGetippt(true)
        }}
        onBlur={(e) => {
          if (!rahmen.current?.contains(e.relatedTarget as Node | null)) {
            schliesse(false)
          }
        }}
      />

      {offen &&
        ort &&
        createPortal(
          <div
            ref={rahmen}
            className="auswahlliste kontobaum"
            style={ort}
          >
            {/* Mehrere Gliederungen, keine Bewertungsebene: der Umschalter (R1047). */}
            {planwahl && (
              <div className="planwahl" role="group" aria-label={t('kontofeld.gliederung')}>
                {quelle.plaene.map((p) => (
                  <button
                    key={p.schluessel}
                    type="button"
                    tabIndex={-1}
                    aria-pressed={p.schluessel === quelle.plan}
                    onMouseDown={(ev) => ev.preventDefault()}
                    onClick={() => quelle.waehlePlan(p.schluessel)}
                  >
                    {mitCode(p.code, beschriftung(p.namen), ' ', p.schluessel)}
                  </button>
                ))}
              </div>
            )}

            {quelle.laedt && zeilen.length === 0 ? (
              <p className="leer" role="status">
                {t('kontofeld.laedt')}
              </p>
            ) : zeilen.length === 0 ? (
              <p className="leer" role="status">
                {t('auswahl.leer', { wort: eingabe.trim() })}
              </p>
            ) : (
              <ul id={baumId} role="tree" aria-label={aria}>
                {/* **Gezeichnet wie der Bericht** (7.2 Abschnitt 55, R1855, R1856). Ein
                    Eintrag ohne Position darüber — „nichts gewählt", die flache
                    Liste — steht wie ein Posten, ohne „·". */}
                {zeilen.map((z, i) => (
                  <Baumzeile
                    key={z.art === 'konto' ? `k:${z.wert}` : `p:${z.schluessel}`}
                    art={z.art === 'konto' && z.ebene > 0 ? 'konto' : 'position'}
                    ebene={z.art === 'konto' && z.ebene > 0 ? z.ebene - 1 : z.ebene}
                    rang={z.art === 'position' ? z.rang : undefined}
                    blass={z.art === 'position' && z.leer === true}
                    pfeil={
                      z.art === 'position'
                        ? {
                            offen: z.offen,
                            hatKinder: z.hatKinder,
                            alsZeichen: true,
                            // Wählt die Zeile, klappt der Pfeil (R1616).
                            aufKlappen: z.waehlbar && z.hatKinder ? () => klappe(z.schluessel) : undefined,
                          }
                        : undefined
                    }
                    klasse={[
                      z.art === 'position' ? 'position' : 'konto',
                      z.art === 'position' && z.leer ? 'leer' : '',
                      i === zeiger ? 'zeiger' : '',
                    ]
                      .filter((k) => k)
                      .join(' ')}
                    id={`${kennung}-z${i}`}
                    role="treeitem"
                    aria-level={z.ebene + 1}
                    aria-expanded={z.art === 'position' && z.hatKinder ? z.offen : undefined}
                    aria-selected={z.art === 'konto' ? z.wert === wert : z.waehlbar ? z.schluessel === wert : undefined}
                    data-wert={z.art === 'konto' ? z.wert : undefined}
                    onMouseDown={(ev) => ev.preventDefault()}
                    onMouseEnter={() => setZeiger(i)}
                    onClick={() => nimm(z)}
                    // Ein Konto trägt kein Menü, auch nicht das des Browsers (R1496).
                    onContextMenu={(ev) => {
                      ev.preventDefault()
                      zeigeMenue(i, ev.clientX, ev.clientY)
                    }}
                  >
                    {z.text}
                    {z.art === 'position' && z.frisch && <span className="vermerk">{t('kontofeld.ohnekonto')}</span>}
                    {/*
                      Der dritte Weg ins Menü, für Touch (R166). Das Zeichen
                      setzt das Gestaltungsblatt — im Text der Zeile hat es
                      nichts verloren, vorgelesen wird die Beschriftung.
                    */}
                    {i === zeiger && anlageort(z) && (
                      <button
                        type="button"
                        className="anlageknopf"
                        tabIndex={-1}
                        aria-label={t('kontofeld.anlegen')}
                        title={t('kontofeld.anlegen')}
                        onMouseDown={(ev) => {
                          ev.preventDefault()
                          ev.stopPropagation()
                        }}
                        onClick={(ev) => {
                          ev.stopPropagation()
                          const r = ev.currentTarget.getBoundingClientRect()
                          zeigeMenue(i, r.left, r.bottom)
                        }}
                      />
                    )}
                  </Baumzeile>
                ))}

                {/* Kam der Baum nicht, steht das am Fuß der flachen Liste (R1210). */}
                {quelle.ausgefallen ? (
                  <li className="listenfuss" onMouseDown={(ev) => ev.preventDefault()}>
                    <Abrufausfall abruf={quelle} satz={quelle.fehler ?? undefined} />
                  </li>
                ) : (
                  quelle.fehler && <li className="listenfuss">{quelle.fehler}</li>
                )}
              </ul>
            )}

            {/*
              **Das Menü steht in der Liste**, damit sie offen bleibt, solange es
              offen ist. Escape nimmt nur das Menü und nicht den Dialog, in dem
              das Feld steht.
            */}
            {menue && (
              <div
                onKeyDown={(ev) => {
                  if (ev.key === 'Escape') {
                    ev.stopPropagation()
                    setMenue(null)
                    feld.current?.focus()
                  }
                }}
              >
                <Zeilenmenue
                  x={menue.x}
                  y={menue.y}
                  punkte={[
                    { schluessel: 'anlage.menue.position', wenn: darfPosition, tun: () => rufeAnlage('position', menue.knoten) },
                    { schluessel: 'anlage.menue.konto', wenn: darfKonto, tun: () => rufeAnlage('konto', menue.knoten) },
                  ]}
                  aufSchliessen={() => {
                    setMenue(null)
                    feld.current?.focus()
                  }}
                />
              </div>
            )}
          </div>,
          document.body,
        )}

      {anlage && createPortal(<Strukturanlage ziel={anlage} aufAngelegt={angelegt} aufSchliessen={anlageZu} />, document.body)}
    </>
  )
}

/** Der Ast für Konten ohne Position (R1045) — ein Schlüssel, den keine Position tragen kann. */
const OHNE = '(ohne-position)'

/** Der Ast der Gewinn- und Verlustrechnung — im Plan gibt es ihn nicht (R1612). */
const GUV = '(guv)'

/** Der Ast der Kapitalflussrechnung mit den Statistikkonten (R1733); seine Positionen hängen den Zeilenschlüssel an. */
const KFR = '(kfr)'

/** Wo ein Statistikkonto hängt: seine Position der Kapitalflussrechnung (R1727). */
type Statistikort = { zeile: string; text: string; sortierung: number }

/**
 * **Wo die Statistikkonten hängen** (2.1 Abschnitt 3.6, R1733): je Konto die
 * Position der Kapitalflussrechnung, aus den Zuordnungen und den Zeilen.
 * Geladen, wenn die Liste aufgeht und Statistikkonten zur Wahl stehen — und
 * jedes Mal neu, damit ein eben angelegtes Konto seinen Ort schon hat.
 */
function useStatistikorte(gebraucht: boolean): Map<string, Statistikort> | null {
  const abruf = useAbruf<Map<string, Statistikort>>(
    gebraucht
      ? () =>
          Promise.all([api.stammdatenliste('kfr_zuordnung'), api.stammdatenliste('kfr_zeile')]).then(([z, k]) => {
            const zeilen = new Map(k.eintraege.map((e) => [e.schluessel, e]))
            const orte = new Map<string, Statistikort>()
            for (const r of z.eintraege) {
              const konto = r.werte['konto']
              const zeile = zeilen.get(r.werte['zeile'] ?? '')
              if (konto && zeile && istStatistikkontoImKatalog(konto)) {
                orte.set(konto, {
                  zeile: zeile.schluessel,
                  text: mitCode(zeile.code, beschriftung(zeile.namen), ' ', zeile.schluessel),
                  sortierung: Number(zeile.werte['sortierung'] ?? 0),
                })
              }
            }
            return orte
          })
      : null,
    [gebraucht],
  )
  return abruf.wert
}

/** Eine Position und alle darunter — aus der Ebene, die das Bild mitbringt. */
function Ast(knoten: Gliederungsknoten[], wurzel: string): Set<string> {
  const ast = new Set<string>()
  const i = knoten.findIndex((k) => k.schluessel === wurzel)
  const oben = knoten[i]
  if (!oben) {
    return ast
  }

  ast.add(oben.schluessel)
  for (let j = i + 1; j < knoten.length; j++) {
    const k = knoten[j]
    if (!k || k.ebene <= oben.ebene) {
      break
    }
    ast.add(k.schluessel)
  }
  return ast
}

type Zeile =
  | {
      art: 'position'
      schluessel: string
      text: string
      ebene: number
      offen: boolean
      hatKinder: boolean
      /** Eben hier angelegt und noch leer (R1497). */
      frisch?: boolean
      /** Nichts darunter ist an dieser Stelle wählbar — blass, aber da (R1612). */
      leer?: boolean
      /** Die Position selbst ist wählbar — im Feld für Position oder Konto (R1616). */
      waehlbar?: boolean
      /** Der Rang wie im Bericht (R570) — ein Ast, den der Bericht nicht kennt, hat keinen. */
      rang?: Rangquelle
    }
  | { art: 'konto'; wert: string; text: string; ebene: number }

/** Eine Position, wie sie im Bericht heißt — ohne Schlüssel davor (R699, R703), mit „· bis MM.JJJJ", wo sie endet (R1725). */
function positionsname(k: Gliederungsknoten): string {
  return mitBis(mitCode(k.code, beschriftung(k.namen), ' ', k.schluessel), k.giltBis)
}

/** Der Weg zum gewählten Konto: seine Position und jede darüber — oder zur gewählten Position. */
function wegZu(knoten: Gliederungsknoten[], ohne: Auswahleintrag[], wert: string, position = false): Set<string> {
  const offen = new Set<string>()
  if (wert === '') {
    return offen
  }

  if (ohne.some((e) => e.wert === wert)) {
    offen.add(OHNE)
    return offen
  }

  const i = knoten.findIndex((k) =>
    position ? k.schluessel === wert : k.konten.some((kt) => kt.schluessel === wert),
  )
  const ziel = knoten[i]
  if (!ziel) {
    return offen
  }

  offen.add(ziel.schluessel)
  if (ziel.art === 'ertrag' || ziel.art === 'aufwand') {
    offen.add(GUV)
  }
  let ebene = ziel.ebene
  for (let j = i - 1; j >= 0 && ebene > 0; j--) {
    const k = knoten[j]
    if (k && k.ebene < ebene) {
      offen.add(k.schluessel)
      ebene = k.ebene
    }
  }
  return offen
}

// Der laufende Monat steht seit dem 2026-10-07 bei der Stilllegung (R1447),
// die ihn als Periode einer Auswahl ohne eigene braucht.
export { laufenderMonat } from './Stilllegung'

const PLANMERKER = 'coko.kontofeld.gliederung'

function gemerkterPlan(): string | null {
  try {
    return window.localStorage.getItem(PLANMERKER)
  } catch {
    return null
  }
}

function merkePlan(plan: string) {
  try {
    window.localStorage.setItem(PLANMERKER, plan)
  } catch {
    // Ohne Ablage bleibt die Wahl nur bis zum Schließen — das ist alles.
  }
}

/*
  **Kurz gemerkt, nicht für immer.** Eine Buchungsmaske mit zehn Zeilen fragte
  sonst zehnmal dieselbe Gliederung ab. Eine halbe Minute genügt dafür — und
  wer die Gliederung inzwischen umgebaut hat, sieht sie beim nächsten Öffnen.
*/
const VORRAT = new Map<string, { zeit: number; antwort: Promise<unknown> }>()
const FRISCH_MS = 30_000

function gemerkt<T>(schluessel: string, holen: () => Promise<T>): Promise<T> {
  const da = VORRAT.get(schluessel)
  if (da && Date.now() - da.zeit < FRISCH_MS) {
    return da.antwort as Promise<T>
  }

  const antwort = holen()
  VORRAT.set(schluessel, { zeit: Date.now(), antwort })
  antwort.catch(() => VORRAT.delete(schluessel))
  return antwort
}

/** Nur für Prüffälle: Jeder Fall beginnt ohne gemerkte Antworten. */
export function vergissGliederungen() {
  VORRAT.clear()
}

/**
 * Woher der Baum kommt (R1047): aus Bewertungsebene und Monat der Stelle —
 * oder, wo keine Ebene feststeht, aus der Gliederung zum laufenden Monat.
 * Geladen wird erst, wenn die Liste zum ersten Mal aufgeht.
 */
function useGliederung(offen: boolean, ledger: string | undefined, periode: number | undefined, stand = 0) {
  const [gebraucht, setGebraucht] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [plaene, setPlaene] = useState<Positionsplan[]>([])
  const [plan, setPlan] = useState<string | null>(null)

  useEffect(() => {
    if (offen) {
      setGebraucht(true)
    }
  }, [offen])

  // Ohne Bewertungsebene: welche Gliederungen es gibt, und welche zuletzt gewählt war.
  //
  // **Ein Hauptabruf** (7.2 Abschnitt 39, R1210): Kam die Liste nicht, steht
  // am Fuß „Ließ sich nicht laden" — bis zum 2026-10-06 stand dort „keine
  // Treffer", als gäbe es kein passendes Konto.
  const planabruf = useAbruf<Positionsplan[]>(
    gebraucht && !ledger ? () => gemerkt('plaene', () => api.positionsplaene()) : null,
    [gebraucht, ledger],
  )

  useEffect(() => {
    const alle = planabruf.wert
    if (alle === null) {
      return
    }
    const merker = gemerkterPlan()
    const vorgabe = alle.find((p) => p.schluessel === merker) ?? alle.find((p) => p.istVorgabe) ?? alle[0]
    setPlaene(alle)
    setPlan(vorgabe?.schluessel ?? null)
    if (!vorgabe) {
      setFehler(t('kontofeld.keinegliederung'))
    }
  }, [planabruf.wert])

  const monat = periode ?? laufenderMonat()
  let anfrage: [string, string | undefined] | null = null
  if (gebraucht && ledger) {
    anfrage = [ledger, undefined]
  } else if (gebraucht && plan) {
    const p = plaene.find((x) => x.schluessel === plan)
    // Mit genanntem Plan zählt die Ebene nicht; der Dienst verlangt trotzdem eine.
    anfrage = [p?.ledger[0] ?? plan, plan]
  }
  const [ebene, gliederung] = anfrage ?? ['', undefined]

  /*
    **Der Baum ist ein Hauptabruf** (7.2 Abschnitt 39, R1211): Kam er nicht,
    bleibt die Liste flach wählbar, und an ihrem Fuß steht „Ließ sich nicht
    laden" mit *Erneut versuchen* — nicht „keine Treffer" (R1210).
  */
  const bildabruf = useAbruf<Gliederungsbild | null>(
    anfrage === null
      ? null
      : () =>
          gemerkt(`bild|${ebene}|${monat}|${gliederung ?? ''}`, () => api.gliederung(ebene, monat, gliederung)).then(
            (b) => {
              setFehler(null)
              return b
            },
            (f) => {
              setFehler(fachlicherSatz(f))
              throw f
            },
          ),
    // `stand` zählt hoch, wenn aus der Liste angelegt wurde (R1493).
    [gebraucht, ledger, periode, plan, plaene, stand],
  )

  return {
    bild: bildabruf.wert,
    fehler,
    laedt: bildabruf.laedt || planabruf.laedt,
    ausgefallen: bildabruf.ausgefallen || planabruf.ausgefallen,
    // Holt neu, was nicht kam — die Liste der Pläne oder den Baum.
    erneut: () => {
      if (planabruf.ausgefallen) {
        planabruf.erneut()
      }
      if (bildabruf.ausgefallen) {
        bildabruf.erneut()
      }
    },
    plaene,
    plan,
    waehlePlan: (schluessel: string) => {
      merkePlan(schluessel)
      setPlan(schluessel)
    },
  }
}
